"use client";

import { useState } from "react";
import { SectionLayout } from "@/components/layout/SectionLayout";
import { SectionTitlte } from "@/components/sections/SectionTitlte";

type ApiResponse = {
  success?: boolean;
  message?: string;
  error?: string;
  id?: string;
};

export const ContactSection = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setServerMessage(null);
    setServerError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          message,
          honeypot,
          formType: "contact",
        }),
      });

      const data: ApiResponse = await response.json();
      if (!response.ok) {
        setServerError(data?.error || "Une erreur est survenue.");
      } else if (data?.success) {
        setServerMessage(
          "Message envoyé avec succès. Nous vous répondrons rapidement."
        );
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setHoneypot("");
        setIsSubmitted(true);
      } else {
        setServerError("Impossible d'envoyer le message.");
      }
    } catch (err) {
      setServerError("Erreur réseau. Veuillez réessayer plus tard.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SectionLayout className="bg-primary py-72" id="contact">
      <div className="flex flex-col items-start gap-6 justify-center w-full">
        <SectionTitlte
          title="Contact"
          subtitle="Contactez-nous via le formulaire ou retrouvez-nous au club."
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
          {/* Left: Form or Success message */}
          <div className="">
            {isSubmitted ? (
              <div className="flex flex-col gap-4">
                <h3 className="text-2xl font-bold text-accent">Merci !</h3>
                <p className="text-accent/75">
                  {serverMessage || "Votre message a bien été envoyé."}
                </p>
                <div
                  className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 text-sm"
                  role="status"
                  aria-live="polite"
                >
                  Message envoyé avec succès.
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsSubmitted(false);
                    setServerMessage(null);
                    setServerError(null);
                  }}
                  className="bg-tertiary text-primary text-sm lg:text-base uppercase px-2 lg:px-6 py-2 lg:py-4 border border-tertiary transition-all duration-300 hover:brightness-95 hover:shadow-[0_8px_20px_rgba(238,121,35,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary w-fit"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
                noValidate
              >
                {/* Honeypot - hidden for bots */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="hp">Ne pas remplir ce champ</label>
                  <input
                    id="hp"
                    type="text"
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="firstName"
                      className="text-sm font-medium text-accent"
                    >
                      Prénom
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      minLength={2}
                      placeholder="Votre prénom"
                      className="w-full border border-[#E2E2E2] px-3 py-3 text-accent placeholder:text-accent/50 focus:outline-none focus:ring-2 focus:ring-accent bg-white"
                      value={firstName}
                      onInvalid={(e) => {
                        const el = e.currentTarget as HTMLInputElement;
                        if (el.validity.valueMissing) {
                          el.setCustomValidity("Le prénom est requis");
                        } else if (el.validity.tooShort) {
                          el.setCustomValidity(
                            "Le prénom doit contenir au moins 2 caractères"
                          );
                        } else {
                          el.setCustomValidity("");
                        }
                      }}
                      onInput={(e) =>
                        (e.currentTarget as HTMLInputElement).setCustomValidity(
                          ""
                        )
                      }
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="lastName"
                      className="text-sm font-medium text-accent"
                    >
                      Nom
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      minLength={2}
                      placeholder="Votre nom"
                      className="w-full border border-[#E2E2E2] px-3 py-3 text-accent placeholder:text-accent/50 focus:outline-none focus:ring-2 focus:ring-accent bg-white"
                      value={lastName}
                      onInvalid={(e) => {
                        const el = e.currentTarget as HTMLInputElement;
                        if (el.validity.valueMissing) {
                          el.setCustomValidity("Le nom est requis");
                        } else if (el.validity.tooShort) {
                          el.setCustomValidity(
                            "Le nom doit contenir au moins 2 caractères"
                          );
                        } else {
                          el.setCustomValidity("");
                        }
                      }}
                      onInput={(e) =>
                        (e.currentTarget as HTMLInputElement).setCustomValidity(
                          ""
                        )
                      }
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-accent"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="vous@exemple.com"
                      className="w-full border border-[#E2E2E2] px-3 py-3 text-accent placeholder:text-accent/50 focus:outline-none focus:ring-2 focus:ring-accent bg-white"
                      value={email}
                      onInvalid={(e) => {
                        const el = e.currentTarget as HTMLInputElement;
                        if (el.validity.valueMissing) {
                          el.setCustomValidity("L'adresse e-mail est requise");
                        } else if (el.validity.typeMismatch) {
                          el.setCustomValidity(
                            "Veuillez saisir une adresse e-mail valide"
                          );
                        } else {
                          el.setCustomValidity("");
                        }
                      }}
                      onInput={(e) =>
                        (e.currentTarget as HTMLInputElement).setCustomValidity(
                          ""
                        )
                      }
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="phone"
                      className="text-sm font-medium text-accent"
                    >
                      Téléphone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      inputMode="tel"
                      autoComplete="tel"
                      pattern="^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$"
                      placeholder="06 12 34 56 78"
                      className="w-full border border-[#E2E2E2] px-3 py-3 text-accent placeholder:text-accent/50 focus:outline-none focus:ring-2 focus:ring-accent bg-white"
                      value={phone}
                      onInvalid={(e) => {
                        const el = e.currentTarget as HTMLInputElement;
                        if (el.validity.valueMissing) {
                          el.setCustomValidity(
                            "Le numéro de téléphone est requis"
                          );
                        } else if (el.validity.patternMismatch) {
                          el.setCustomValidity(
                            "Veuillez saisir un numéro de téléphone français valide"
                          );
                        } else {
                          el.setCustomValidity("");
                        }
                      }}
                      onInput={(e) =>
                        (e.currentTarget as HTMLInputElement).setCustomValidity(
                          ""
                        )
                      }
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="message"
                    className="text-sm font-medium text-accent"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    minLength={5}
                    rows={6}
                    placeholder="Votre message..."
                    className="w-full border border-[#E2E2E2] px-3 py-3 text-accent placeholder:text-accent/50 focus:outline-none focus:ring-2 focus:ring-accent bg-white resize-none"
                    value={message}
                    onInvalid={(e) => {
                      const el = e.currentTarget as HTMLTextAreaElement;
                      if (el.validity.valueMissing) {
                        el.setCustomValidity("Le message est requis");
                      } else if (el.validity.tooShort) {
                        el.setCustomValidity(
                          "Le message doit contenir au moins 5 caractères"
                        );
                      } else {
                        el.setCustomValidity("");
                      }
                    }}
                    onInput={(e) =>
                      (
                        e.currentTarget as HTMLTextAreaElement
                      ).setCustomValidity("")
                    }
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>

                {serverMessage && (
                  <div
                    className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 text-sm"
                    role="status"
                    aria-live="polite"
                  >
                    {serverMessage}
                  </div>
                )}
                {serverError && (
                  <div
                    className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm"
                    role="alert"
                    aria-live="assertive"
                  >
                    {serverError}
                  </div>
                )}

                <div className="flex items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-tertiary text-primary text-sm lg:text-base uppercase px-2 lg:px-6 py-2 lg:py-4 border border-tertiary transition-all duration-300 hover:brightness-95 hover:shadow-[0_8px_20px_rgba(238,121,35,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary w-fit disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Envoi..." : "Envoyer"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Right: Google Map */}
          <div className="bg-primary border border-[#E2E2E2] p-0 h-[360px] md:h-full">
            <iframe
              title="Carte du TCAB - 7 bis Rue Suzanne Lenglen, 60000 Beauvais"
              className="w-full h-full min-h-[360px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=TCAB-Tennis%20Club%20de%20l%27Agglom%C3%A9ration%20de%20Beauvais%2C%207%20bis%20Rue%20Suzanne%20Lenglen%2C%2060000%20Beauvais&hl=fr&z=17&output=embed"
            />
          </div>
        </div>
      </div>
    </SectionLayout>
  );
};
